import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";
import { handleDownloadSizeProgress } from "../../Layouts/Navbar/DownloadProgress/DownloadLogSlice";

const initialState = {
  isFetchingPcapStart: false,

  pcapData: [],
  isFetchingPcapData: false,

  isFetchingPcapDownload: false,
};

export const pcapStart = createAsyncThunk(
  "pcaps/start",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const { ip, port, systemId, duration } = udata;
      const formData = new URLSearchParams();
      if (ip !== undefined) formData.append("ip", ip);
      if (port !== undefined) formData.append("port", port);
      if (systemId !== undefined) formData.append("systemId", systemId);
      if (duration !== undefined) formData.append("duration", duration);

      const { data, status } = await axios.post(
        "/pcaps/start?" + formData.toString(),
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error(err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
const normalizePcapDateTime = (value) => {
  if (!value) return value;
  if (value instanceof Date) {
    const pad = (num, size = 2) => String(num).padStart(size, "0");
    const ms = String(value.getMilliseconds()).padStart(3, "0");
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
      value.getDate()
    )}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(
      value.getSeconds()
    )}.${ms}`;
  }

  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  const normalized = trimmed.replace(" ", "T");
  const [datePart, timeWithZone] = normalized.split("T");
  if (!timeWithZone) return normalized;

  const zoneMatch = timeWithZone.match(/(Z|[+-]\d{2}:\d{2})$/);
  const timeOnly = zoneMatch
    ? timeWithZone.slice(0, zoneMatch.index)
    : timeWithZone;

  if (timeOnly.includes(".")) {
    const [timePart, msPart] = timeOnly.split(".");
    const paddedMs = msPart.padEnd(3, "0").slice(0, 3);
    return `${datePart}T${timePart}.${paddedMs}`;
  }

  if (/^\d{2}:\d{2}$/.test(timeOnly)) {
    return `${datePart}T${timeOnly}:00.000`;
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(timeOnly)) {
    return `${datePart}T${timeOnly}.000`;
  }

  return `${datePart}T${timeOnly}`;
};

export const getAllPCaps = createAsyncThunk(
  "pcaps/all",
  async (
    {
      udata,
      search,
      page,
      size,
      sortBy,
      sortDirection,
      download = false,
      controller,
    },
    thunkAPI
  ) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();
      const { clientIp, from, to, duration } = udata;
      const normalizedFrom = normalizePcapDateTime(from);
      const normalizedTo = normalizePcapDateTime(to);

      if (search) urlParams.append("search", search);
      if (clientIp) urlParams.append("clientIp", clientIp);
      if (normalizedFrom) urlParams.append("from", normalizedFrom);
      if (normalizedTo) urlParams.append("to", normalizedTo);
      if (duration) urlParams.append("duration", duration);
      if (sortBy) urlParams.append("sortBy", sortBy);
      if (sortDirection) urlParams.append("sortDirection", sortDirection);

      urlParams.append("page", page || 0);
      urlParams.append("size", size || 20);

      const { data, status } = await axios.get(
        "/pcaps?" + urlParams.toString(),
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
          ...(download
            ? {
                signal: controller.signal,
                onDownloadProgress: (progressEvent) => {
                 
                  const downloadedBytes = progressEvent.loaded;
                  thunkAPI.dispatch(
                    handleDownloadSizeProgress({
                      id: download,
                      size: downloadedBytes * 1.05,
                    })
                  );
                },
              }
            : {}),
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const downloadPcapFiles = createAsyncThunk(
  "/pcaps/download",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const urlParams = new URLSearchParams();

      if (udata) urlParams.append("id", udata);

      const { data, status, headers } = await axios.get(
        `/pcaps/download?` + urlParams.toString(),
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            Accept: "application/gzip",
          },
        }
      );

      if (status.toString().startsWith("2")) {
        const contentDisposition = headers.get("Content-Disposition");
        let filename = "pcap.zip"; 

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename); 
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        data: err.response.data,
        status: err.response.status,
      });
    }
  }
);

export const pcapSlice = createSlice({
  name: "pcap",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(pcapStart.fulfilled, (state) => {
        state.isFetchingPcapStart = false;
      })
      .addCase(pcapStart.pending, (state, action) => {
        state.isFetchingPcapStart =
          action.meta.arg.ip +
          "-" +
          action.meta.arg.port +
          "-" +
          action.meta.arg.systemId;
      })
      .addCase(pcapStart.rejected, (state) => {
        state.isFetchingPcapStart = false;
      })
      .addCase(getAllPCaps.fulfilled, (state, action) => {
        state.pcapData = action.payload.data.data;
        state.isFetchingPcapData = false;
      })
      .addCase(getAllPCaps.pending, (state) => {
        state.isFetchingPcapData = true;
      })
      .addCase(getAllPCaps.rejected, (state) => {
        state.pcapData = [];
        state.isFetchingPcapData = false;
      })
      .addCase(downloadPcapFiles.pending, (state, action) => {
        state.isFetchingPcapDownload = action.meta?.arg;
      })
      .addCase(downloadPcapFiles.fulfilled, (state) => {
        state.isFetchingPcapDownload = false;
      })
      .addCase(downloadPcapFiles.rejected, (state) => {
        state.isFetchingPcapDownload = false;
      });
  },
});

export const pCapSelector = (state) => state.pcap;

export default pcapSlice.reducer;
